/**
 * navigation/rootNavigator.tsx
 * Top-level gate. SK Catalog does NOT own authentication — the SK Platform owns
 * the only login / signup / forgot-password / profile screens. So this gate
 * only answers "is there a valid, fully-onboarded platform session?":
 *
 *   • If the catalog verification email landed here (?mode=verify&token=…),
 *     consume the token first (verifies email + activates org + logs in).
 *   • probe GET /v1/me (httpOnly cookie, via Backbone → platform):
 *       200 + profile complete   → app shell
 *       200 + profile INCOMPLETE  → bounce to the platform to finish Step 2
 *                                   ("Tell us about you"); the platform returns
 *                                   here after "Go Live" via the product handoff
 *       401 / error               → bounce to the centralized platform auth
 *
 * Sign-out clears the session and hands back to the platform auth.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';

import { useTheme } from '../context/themeContext';
import { getCurrentUser, logoutUser, verifyEmail } from '../services/auth/auth';

import { VoiceConsoleProvider } from '../context/voiceConsoleContext';
import { LiveCallsProvider } from '../context/liveCallsContext';
import VoiceAppShell from '../shell/VoiceAppShell';
import { redirectToNotInPlan, redirectToPlatformAuth, takeVerifyToken } from './platformAuthRedirect';
import { isNotInPlan } from '../lib/planGate';

type Phase = 'booting' | 'app' | 'error';

// On localhost (local dev + the product-testing-website handoff) there's no
// platform session, and SK Catalog's screens are fully driven by
// data/mockData.ts. So instead of bouncing an unauthenticated visitor to the
// SK Platform login, open straight into the seeded demo app. Real deployed
// hosts keep the centralized platform auth handoff unchanged.
const IS_LOCAL_DEMO =
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

export default function RootNavigator() {
  const { theme } = useTheme();
  const { colors } = theme;

  const [phase, setPhase] = useState<Phase>('booting');

  // Boot decision. Three outcomes: a fully-onboarded session (→ app), a genuine
  // auth failure / pending onboarding (→ hand off to the platform), or the auth
  // backend being unreachable (→ a retryable error screen — see the catch).
  const runBoot = useCallback(async () => {
    setPhase('booting');
    // No valid session → the platform owns login. Bounce there (even on
    // localhost) so we never render an unauthenticated shell whose every API
    // call 401s. Demo-without-backend is handled in the catch, only when the
    // auth backend is genuinely unreachable.
    const handoffOrDemo = () => {
      redirectToPlatformAuth();
    };
    try {
      // The catalog verification email links back here. Consume the token so
      // the email is verified + the org activated + the user logged in. The
      // platform also returns profile_complete; we re-read it from /me below.
      const verifyToken = takeVerifyToken();
      if (verifyToken) {
        try { await verifyEmail(verifyToken); } catch { /* expired/used → fall through to /me */ }
      }

      const me: any = await getCurrentUser();
      const user = me?.data?.user ?? me?.user;
      if (!user) { handoffOrDemo(); return; }
      // SoftKnack staff work on every org's setup, not their own — no profile step, no plan.
      const isPlatformAdmin = (me?.data ?? me)?.is_platform_admin === true;
      // Verified but profile (Step 2) still pending → finish it on the
      // platform, which owns the "Tell us about you" card and returns here.
      if (user.profile_complete === false && !isPlatformAdmin) { handoffOrDemo(); return; }
      // Signed in, but this org's plan does not include the Voice website
      // (SK Fleet / Salon use Voice underneath without selling it). No list = open.
      if (!isPlatformAdmin && isNotInPlan((me?.data ?? me)?.subscribed_products)) { redirectToNotInPlan(); return; }
      setPhase('app');
    } catch (err: any) {
      // CRITICAL: tell "auth backend unreachable" apart from "not logged in".
      // A real 401/403 carries an HTTP response → genuinely unauthenticated, so
      // hand off to the platform login. A transport failure (platform down, DNS,
      // CORS, connection refused) has NO `response` — and must NOT be read as
      // "logged out": that bounces to the platform, which (with a valid session)
      // bounces right back here → an infinite redirect loop on a blank screen.
      // Surface an honest, retryable error instead.
      // A real 401/403 carries an HTTP response → genuinely not logged in →
      // hand off to the platform login (even on localhost). Only a transport
      // failure (no response: platform down / DNS / connection refused) falls
      // back — to the seeded demo app on localhost, or a retryable error screen.
      if (err?.response) { redirectToPlatformAuth(); return; }
      if (IS_LOCAL_DEMO) { setPhase('app'); return; }
      setPhase('error');
    }
  }, []);

  useEffect(() => { void runBoot(); }, [runBoot]);

  // Sign out → clear the platform session, then hand back to the platform auth.
  const handleSignOut = async () => {
    try { await logoutUser(); } finally { redirectToPlatformAuth(); }
  };

  if (phase === 'app') {
    // Authenticated + onboarded → the console. VoiceConsoleProvider drives the
    // role/screen/tenant UI state (the design prototype's model); the real
    // signed-in identity comes from SessionProvider above. onSignOut is held for
    // the shell's logout affordance (wired next to the topbar profile menu).
    return (
      <VoiceConsoleProvider>
        {/* One SSE subscription for the whole app — mounted here so it survives
            screen/tab switches (the live call no longer vanishes on navigation). */}
        <LiveCallsProvider>
          <VoiceAppShell onSignOut={handleSignOut} />
        </LiveCallsProvider>
      </VoiceConsoleProvider>
    );
  }

  if (phase === 'error') {
    return (
      <View style={[styles.boot, { backgroundColor: colors.paper }]}>
        <Text style={[styles.errTitle, { color: colors.ink }]}>Can’t reach the sign-in service</Text>
        <Text style={[styles.errBody, { color: colors.ink3 }]}>
          We couldn’t confirm your session — the authentication service didn’t respond.
          This usually means a required backend isn’t running. Your login is fine; try again once it’s back up.
        </Text>
        <Pressable
          onPress={() => { void runBoot(); }}
          style={[styles.retryBtn, { backgroundColor: colors.accent }]}
          accessibilityRole="button"
        >
          <Text style={[styles.retryLabel, { color: colors.paper }]}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  // 'booting' (or redirecting away) — show a spinner.
  return (
    <View style={[styles.boot, { backgroundColor: colors.paper }]}>
      <ActivityIndicator size="large" color={colors.accent} />
    </View>
  );
}

const styles = StyleSheet.create({
  boot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { height: '100vh' as any } : {}),
  },
  errTitle: { fontSize: 16, fontWeight: '600', marginBottom: 8, textAlign: 'center' },
  errBody: { fontSize: 13, lineHeight: 19, textAlign: 'center', maxWidth: 380, marginBottom: 22, paddingHorizontal: 24 },
  retryBtn: { paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8 },
  retryLabel: { fontSize: 14, fontWeight: '600' },
});
